import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleBySlug, getCategoryBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const category = article ? getCategoryBySlug(article.category) : null;
  const relatedArticles = article ? getRelatedArticles(article.slug, article.category, 3) : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">文章未找到</p>
        </div>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '害羞草指南',
    },
    publisher: {
      '@type': 'Organization',
      name: '害羞草指南',
      url: 'https://www.mimosa-guide.com/',
    },
    inLanguage: 'zh-CN',
    image: article.imageUrl,
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        canonicalUrl={`https://www.mimosa-guide.com/article/${article.slug}`}
        ogType="article"
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  ...(category ? [{ label: category.name, path: `/category/${category.slug}` }] : []),
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <article className="w-full px-4 md:px-6 py-4 md:py-8">
            <div className="max-w-3xl mx-auto">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full cursor-pointer hover:bg-primary-100 transition-colors whitespace-nowrap"
                    >
                      {category.name}
                    </Link>
                  )}
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="text-xs text-foreground-400">阅读约 {article.readTime}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>
                <p className="text-base text-foreground-500 leading-relaxed">
                  {article.excerpt}
                </p>
              </div>

              <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={`${article.title} - 害羞草指南`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                  prose-h4:text-xl prose-h4:mt-8 prose-h4:mb-4
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-base
                  prose-strong:text-foreground-900
                  prose-em:text-foreground-600
                  [&_h4]:font-heading [&_h4]:text-foreground-900 [&_h4]:font-semibold [&_h4]:text-xl [&_h4]:mt-8 [&_h4]:mb-4
                  [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-base
                  [&_strong]:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.contentHtml }}
              />
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="max-w-6xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
                  相关文章推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((rel) => (
                    <ArticleCard
                      key={rel.slug}
                      slug={rel.slug}
                      title={rel.title}
                      excerpt={rel.excerpt}
                      imageUrl={rel.imageUrl}
                      date={rel.date}
                      readTime={rel.readTime}
                      categoryName={category?.name}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}